/**
 * CookieConsentBanner
 *
 * GDPR/DPDP-compliant cookie consent banner shown to first-time visitors.
 * Preference is stored in localStorage under 'cookie_consent'.
 *
 * Values:
 *   'all'       — user accepted all cookies
 *   'essential' — user declined non-essential cookies
 *   (absent)    — user has not yet made a choice; banner is shown
 */

import React, { useState, useEffect } from 'react';
import { Cookie, X, ChevronDown, ChevronUp } from 'lucide-react';

const STORAGE_KEY = 'cookie_consent';

type ConsentValue = 'all' | 'essential';

interface CookieCategory {
  id: string;
  name: string;
  description: string;
  required: boolean;
}

const categories: CookieCategory[] = [
  {
    id: 'essential',
    name: 'Essential',
    description:
      'Required for the platform to function. Includes session authentication, security tokens, and UI preference storage. Cannot be disabled.',
    required: true,
  },
  {
    id: 'analytics',
    name: 'Analytics',
    description:
      'Help us understand how users interact with Marichi so we can improve the product. Data is aggregated and anonymised.',
    required: false,
  },
  {
    id: 'marketing',
    name: 'Marketing',
    description:
      'Used to measure the effectiveness of our campaigns and show relevant content to prospective users on third-party platforms.',
    required: false,
  },
];

export const CookieConsentBanner: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      // Small delay so the banner doesn't flash on initial paint
      const timer = setTimeout(() => setVisible(true), 600);
      return () => clearTimeout(timer);
    }
  }, []);

  const save = (value: ConsentValue) => {
    localStorage.setItem(STORAGE_KEY, value);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label="Cookie consent"
      className="fixed bottom-0 left-0 right-0 z-50 p-4 sm:p-6"
    >
      <div className="max-w-4xl mx-auto bg-card dark:bg-card-darkAlt rounded-2xl shadow-2xl border border-line dark:border-border-dark overflow-hidden">
        {/* Main row */}
        <div className="p-5 sm:p-6">
          <div className="flex items-start gap-4">
            {/* Icon */}
            <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Cookie className="w-5 h-5 text-primary" />
            </div>

            {/* Text */}
            <div className="flex-1 min-w-0">
              <h2 className="text-base font-semibold text-ink leading-snug">
                We use cookies
              </h2>
              <p className="mt-1 text-sm text-ink-2 leading-relaxed">
                We use essential cookies to keep the platform running and, with your consent, analytics
                and marketing cookies to improve your experience and measure our campaigns.{' '}
                <a
                  href="/cookie-policy"
                  className="text-primary hover:underline font-medium"
                >
                  Cookie Policy
                </a>
              </p>

              {/* Expandable categories */}
              <button
                onClick={() => setExpanded((v) => !v)}
                className="mt-2 flex items-center gap-1 text-xs font-medium text-ink-3 hover:text-ink-2 transition-colors"
                aria-expanded={expanded}
              >
                {expanded ? (
                  <>
                    <ChevronUp className="w-3.5 h-3.5" />
                    Hide details
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-3.5 h-3.5" />
                    Manage preferences
                  </>
                )}
              </button>

              {expanded && (
                <div className="mt-3 space-y-3">
                  {categories.map((cat) => (
                    <div
                      key={cat.id}
                      className="flex items-start gap-3 p-3 rounded-lg bg-ground"
                    >
                      <div className="flex-1">
                        <p className="text-sm font-medium text-ink">
                          {cat.name}
                          {cat.required && (
                            <span className="ml-2 text-xs font-normal text-ink-3">
                              Always active
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-ink-3 mt-0.5 leading-relaxed">
                          {cat.description}
                        </p>
                      </div>
                      {/* Toggle indicator */}
                      <div
                        className={`flex-shrink-0 mt-0.5 w-8 h-4.5 rounded-full ${
                          cat.required
                            ? 'bg-primary'
                            : 'bg-gray-300'
                        }`}
                        aria-label={cat.required ? 'Enabled' : 'Disabled when declining non-essential cookies'}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Close — only dismisses visually; does not save a preference so banner returns on next visit */}
            <button
              onClick={() => setVisible(false)}
              className="flex-shrink-0 p-1.5 text-ink-3 hover:text-ink-2 rounded-lg hover:bg-inset transition-colors"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Action buttons */}
          <div className="mt-4 flex flex-wrap gap-2 sm:justify-end">
            <button
              onClick={() => save('essential')}
              className="flex-1 sm:flex-none px-4 py-2 text-sm font-medium rounded-lg border border-line-2 dark:border-border-dark text-ink-2 hover:bg-ground transition-colors"
            >
              Essential only
            </button>
            <button
              onClick={() => save('all')}
              className="flex-1 sm:flex-none px-4 py-2 text-sm font-medium rounded-lg bg-primary text-white hover:bg-primary/90 transition-colors"
            >
              Accept all
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
